import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  Shield, Users, Receipt, Store, TrendingUp, 
  AlertTriangle, Package, Settings, ArrowLeft, BarChart3,
  Activity, Eye, Trash2, Ban, CheckCircle2, MessageSquare, Clock, Mail, Reply,
  ImageIcon, X, Bug, GitBranch, Plus, Edit2, Save
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";
import AdminShopTab from "@/components/AdminShopTab";
import { APP_VERSION } from "@/config/version";

const AdminDashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [replyingTicket, setReplyingTicket] = useState(null);
  const [replyText, setReplyText] = useState("");

  const [smtpConfig, setSmtpConfig] = useState({ host: "", port: 465, email: "", password: "" });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);

  // Product recalls
  const [recalls, setRecalls] = useState([]);
  const [newRecall, setNewRecall] = useState({ product_name: "", brand: "", reason: "", barcode: "", images: [] });
  const [sendingRecall, setSendingRecall] = useState(false);

  // Versions & Bugs
  const [versions, setVersions] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [newVersion, setNewVersion] = useState({ version: "", release_date: "", changes: "", is_major: false });
  const [newBug, setNewBug] = useState({ title: "", description: "", severity: "medium", source_ticket_id: "" });
  const [editingBug, setEditingBug] = useState(null);

  useEffect(() => {
    // Check if user is admin
    if (!user?.is_admin) {
      toast.error("Accès non autorisé");
      navigate("/");
      return;
    }
    
    fetchData();
  }, [user, navigate]);

  const handleDeleteUser = async (userId, pseudo) => {
    if (!window.confirm(`Supprimer définitivement l'utilisateur "${pseudo}" et toutes ses données ?`)) return;
    setDeletingId(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${pseudo} supprimé`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setStats(prev => prev ? { ...prev, totalUsers: prev.totalUsers - 1 } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeletingId(null);
    }
  };

  const handleTicketReply = async (ticketId) => {
    if (!replyText.trim()) return;
    try {
      await axios.put(`${API}/admin/support-tickets/${ticketId}`, {
        admin_reply: replyText.trim(),
        status: "replied",
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Réponse envoyée");
      setReplyingTicket(null);
      setReplyText("");
      fetchData();
    } catch {
      toast.error("Erreur");
    }
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      await axios.put(`${API}/admin/support-tickets/${ticketId}`, {
        status: "closed",
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Ticket fermé");
      fetchData();
    } catch {
      toast.error("Erreur");
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, recipesRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/feed`),
        axios.get(`${API}/admin/support-tickets`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);

      setUsers(usersRes.data);
      setRecipes(recipesRes.data);
      setTickets(ticketsRes.data);
      
      const businessCount = usersRes.data.filter(u => u.is_business).length;
      setStats({
        totalUsers: usersRes.data.length,
        totalRecipes: recipesRes.data.length,
        totalBusinesses: businessCount,
        activeToday: Math.floor(usersRes.data.length * 0.3),
        openTickets: ticketsRes.data.filter(t => t.status === "open").length,
      });

      // Fetch SMTP config, recalls, versions & bugs in parallel
      const [smtpRes, recallsRes, versionsRes, bugsRes] = await Promise.all([
        axios.get(`${API}/admin/smtp-config`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        axios.get(`${API}/admin/product-recalls`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/versions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/bugs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      if (smtpRes?.data) setSmtpConfig(smtpRes.data);
      setRecalls(recallsRes?.data || []);
      setVersions(versionsRes?.data || []);
      setBugs(bugsRes?.data || []);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Erreur chargement données admin");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 pt-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>

          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">
                Administration Gusto
              </h1>
              <p className="text-white/80 text-sm">
                Gestion complète de la plateforme
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-4 -mt-12 max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal">{stats?.totalUsers || 0}</p>
                  <p className="text-xs text-muted-foreground">Utilisateurs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <GustoIcon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal">{stats?.totalChefs || 0}</p>
                  <p className="text-xs text-muted-foreground">Chefs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal">{stats?.totalRecipes || 0}</p>
                  <p className="text-xs text-muted-foreground">Recettes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal">{stats?.activeToday || 0}</p>
                  <p className="text-xs text-muted-foreground">Actifs aujourd'hui</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 mt-6 max-w-6xl mx-auto">
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="grid w-full grid-cols-8 mb-6">
            <TabsTrigger value="users">Utilisateurs</TabsTrigger>
            <TabsTrigger value="tickets" className="relative">
              Tickets
              {stats?.openTickets > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {stats.openTickets}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="recipes">Recettes</TabsTrigger>
            <TabsTrigger value="shop">Boutique</TabsTrigger>
            <TabsTrigger value="recalls">Rappels</TabsTrigger>
            <TabsTrigger value="versions">Versions</TabsTrigger>
            <TabsTrigger value="bugs" className="relative">
              Bugs
              {bugs.filter(b => b.status === "open").length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {bugs.filter(b => b.status === "open").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings">Email</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Liste des utilisateurs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((u, index) => (
                    <div 
                      key={u.id || index}
                      className="flex items-center justify-between p-3 rounded-xl bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                          <GustoIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-charcoal">{u.pseudo}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{u.recipe_count} recettes</span>
                        <span className="text-xs text-amber-600 font-medium">{u.total_points} pts</span>
                        {u.is_admin ? (
                          <Badge className="bg-purple-100 text-purple-700">Admin</Badge>
                        ) : u.is_business ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Pro</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700">Actif</Badge>
                        )}
                        <Link to={`/user/${u.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        {!u.is_admin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(u.id, u.pseudo)}
                            disabled={deletingId === u.id}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Tickets de support ({tickets.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tickets.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Aucun ticket</p>
                ) : (
                  <div className="space-y-3">
                    {tickets.map((t) => (
                      <div key={t.id} className="p-4 rounded-xl bg-gray-50 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={t.status === "open" ? "bg-red-100 text-red-700" : t.status === "replied" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}>
                              {t.status === "open" ? "Ouvert" : t.status === "replied" ? "Répondu" : "Fermé"}
                            </Badge>
                            <Badge variant="outline" className="text-xs">{t.category}</Badge>
                            <span className="text-xs text-muted-foreground">{t.ticket_number}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString("fr-FR")}</span>
                        </div>
                        <p className="font-medium text-sm text-charcoal">{t.subject}</p>
                        <p className="text-xs text-muted-foreground">{t.name} — {t.email}</p>
                        <p className="text-sm text-muted-foreground bg-white rounded-lg p-3">{t.message}</p>
                        {t.admin_reply && (
                          <div className="bg-blue-50 rounded-lg p-3 border-l-2 border-blue-400">
                            <p className="text-xs font-medium text-blue-800 mb-1">Réponse admin :</p>
                            <p className="text-sm text-blue-700">{t.admin_reply}</p>
                          </div>
                        )}
                        <div className="flex gap-2 pt-1">
                          {replyingTicket === t.id ? (
                            <div className="flex-1 space-y-2">
                              <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Votre réponse..."
                                rows={3}
                                className="w-full rounded-md border p-2 text-sm"
                              />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleTicketReply(t.id)} className="rounded-full gap-1">
                                  <Reply className="w-3 h-3" /> Envoyer
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { setReplyingTicket(null); setReplyText(""); }} className="rounded-full">
                                  Annuler
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {t.status !== "closed" && (
                                <Button size="sm" variant="outline" onClick={() => setReplyingTicket(t.id)} className="rounded-full gap-1">
                                  <Reply className="w-3 h-3" /> Répondre
                                </Button>
                              )}
                              {t.status !== "closed" && (
                                <Button size="sm" variant="ghost" onClick={() => handleCloseTicket(t.id)} className="rounded-full text-muted-foreground gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> Fermer
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recipes Tab */}
          <TabsContent value="recipes">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GustoIcon className="w-5 h-5" />
                  Recettes récentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recipes.slice(0, 10).map((recipe, index) => (
                    <div 
                      key={recipe.id || index}
                      className="flex items-center justify-between p-3 rounded-xl bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center overflow-hidden">
                          {recipe.final_image ? (
                            <img 
                              src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                              alt={recipe.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <GustoIcon className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-charcoal">{recipe.name}</p>
                          <p className="text-sm text-muted-foreground">par {recipe.chef_pseudo}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {recipe.reproductions_count || 0} repros
                        </Badge>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shop Tab */}
          <TabsContent value="shop">
            <AdminShopTab token={token} />
          </TabsContent>

          {/* Product Recalls Tab */}
          <TabsContent value="recalls">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Alertes rappels produits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 space-y-3">
                  <h3 className="font-semibold text-red-800">Créer une alerte</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      className="border rounded-lg px-3 py-2 text-sm"
                      placeholder="Nom du produit *"
                      value={newRecall.product_name}
                      onChange={(e) => setNewRecall(p => ({ ...p, product_name: e.target.value }))}
                      data-testid="recall-product-name"
                    />
                    <input
                      className="border rounded-lg px-3 py-2 text-sm"
                      placeholder="Marque"
                      value={newRecall.brand}
                      onChange={(e) => setNewRecall(p => ({ ...p, brand: e.target.value }))}
                    />
                  </div>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Motif du rappel *"
                    rows={2}
                    value={newRecall.reason}
                    onChange={(e) => setNewRecall(p => ({ ...p, reason: e.target.value }))}
                    data-testid="recall-reason"
                  />

                  {/* Photo upload */}
                  <div>
                    <label className="text-sm font-medium text-red-800 mb-1 block">Photos du produit (optionnel)</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {newRecall.images.map((img, i) => (
                        <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                          <button
                            className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                            onClick={() => setNewRecall(p => ({ ...p, images: p.images.filter((_, j) => j !== i) }))}
                          >&times;</button>
                        </div>
                      ))}
                      <label
                        className="w-20 h-20 rounded-lg border-2 border-dashed border-red-300 flex flex-col items-center justify-center cursor-pointer hover:bg-red-50 transition-colors"
                        data-testid="recall-upload-photo"
                      >
                        <ImageIcon className="w-5 h-5 text-red-400" />
                        <span className="text-[10px] text-red-400 mt-0.5">Ajouter</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            files.forEach(file => {
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                const img = new Image();
                                img.onload = () => {
                                  const canvas = document.createElement("canvas");
                                  const MAX = 800;
                                  let w = img.width, h = img.height;
                                  if (w > MAX || h > MAX) {
                                    if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
                                    else { w = Math.round(w * MAX / h); h = MAX; }
                                  }
                                  canvas.width = w; canvas.height = h;
                                  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
                                  const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
                                  setNewRecall(p => ({ ...p, images: [...p.images, dataUrl] }));
                                };
                                img.src = ev.target.result;
                              };
                              reader.readAsDataURL(file);
                            });
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    className="rounded-full gap-2"
                    disabled={sendingRecall || !newRecall.product_name || !newRecall.reason}
                    onClick={async () => {
                      setSendingRecall(true);
                      try {
                        const res = await axios.post(`${API}/admin/product-recalls`, newRecall, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        toast.success(res.data.message);
                        setNewRecall({ product_name: "", brand: "", reason: "", barcode: "", images: [] });
                        setRecalls(prev => [res.data.recall, ...prev]);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Erreur");
                      } finally { setSendingRecall(false); }
                    }}
                    data-testid="send-recall-btn"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    {sendingRecall ? "Envoi..." : "Envoyer l'alerte à tous les utilisateurs"}
                  </Button>
                </div>

                {recalls.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground">Historique ({recalls.length})</h3>
                    {recalls.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{r.product_name}{r.brand ? ` (${r.brand})` : ""}</p>
                          <p className="text-xs text-muted-foreground">{r.reason}</p>
                          {r.images?.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {r.images.map((img, i) => (
                                <img key={i} src={img} alt="" className="w-10 h-10 rounded object-cover border" />
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
                        </div>
                        <Button
                          variant="ghost" size="sm"
                          onClick={async () => {
                            await axios.delete(`${API}/admin/product-recalls/${r.id}`, { headers: { Authorization: `Bearer ${token}` } });
                            setRecalls(prev => prev.filter(x => x.id !== r.id));
                            toast.success("Supprimé");
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email/SMTP Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Configuration Email (SMTP SSL)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Hôte SMTP</label>
                    <input
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      placeholder="smtp.example.com"
                      value={smtpConfig.host}
                      onChange={(e) => setSmtpConfig(p => ({ ...p, host: e.target.value }))}
                      data-testid="smtp-host"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Port</label>
                    <input
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      type="number"
                      value={smtpConfig.port}
                      onChange={(e) => setSmtpConfig(p => ({ ...p, port: parseInt(e.target.value) || 465 }))}
                      data-testid="smtp-port"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Email expéditeur</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="contact@gusto.fr"
                    value={smtpConfig.email}
                    onChange={(e) => setSmtpConfig(p => ({ ...p, email: e.target.value }))}
                    data-testid="smtp-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Mot de passe SMTP</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    type="password"
                    placeholder="••••••••"
                    value={smtpConfig.password}
                    onChange={(e) => setSmtpConfig(p => ({ ...p, password: e.target.value }))}
                    data-testid="smtp-password"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    className="rounded-full gap-2"
                    disabled={smtpLoading}
                    onClick={async () => {
                      setSmtpLoading(true);
                      try {
                        await axios.put(`${API}/admin/smtp-config`, smtpConfig, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        toast.success("Configuration SMTP sauvegardée");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Erreur");
                      } finally { setSmtpLoading(false); }
                    }}
                    data-testid="save-smtp-btn"
                  >
                    <Settings className="w-4 h-4" />
                    {smtpLoading ? "Sauvegarde..." : "Sauvegarder"}
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full gap-2"
                    disabled={smtpTesting}
                    onClick={async () => {
                      setSmtpTesting(true);
                      try {
                        const res = await axios.post(`${API}/admin/smtp-test`, {}, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        toast.success(res.data.message);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Échec du test");
                      } finally { setSmtpTesting(false); }
                    }}
                    data-testid="test-smtp-btn"
                  >
                    <Mail className="w-4 h-4" />
                    {smtpTesting ? "Test..." : "Envoyer un test"}
                  </Button>
                </div>

                {smtpConfig.configured && (
                  <div className="p-3 rounded-xl bg-green-50 border border-green-200 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">SMTP configuré — les emails sont actifs</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Versions Tab */}
          <TabsContent value="versions">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-blue-600" />
                  Suivi des versions
                  <Badge className="ml-auto bg-blue-100 text-blue-700">v{APP_VERSION} actuelle</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new version form */}
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 space-y-4">
                  <h4 className="font-semibold text-blue-800">Ajouter une nouvelle version</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Numéro de version</Label>
                      <Input
                        placeholder="1.6.0"
                        value={newVersion.version}
                        onChange={(e) => setNewVersion({...newVersion, version: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Date de sortie</Label>
                      <Input
                        type="date"
                        value={newVersion.release_date}
                        onChange={(e) => setNewVersion({...newVersion, release_date: e.target.value})}
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newVersion.is_major}
                          onChange={(e) => setNewVersion({...newVersion, is_major: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Version majeure</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label>Changements (un par ligne)</Label>
                    <Textarea
                      placeholder="Ajout de la fonctionnalité X&#10;Correction du bug Y"
                      rows={4}
                      value={newVersion.changes}
                      onChange={(e) => setNewVersion({...newVersion, changes: e.target.value})}
                    />
                  </div>
                  <Button
                    onClick={async () => {
                      if (!newVersion.version) {
                        toast.error("Numéro de version requis");
                        return;
                      }
                      try {
                        await axios.post(`${API}/admin/versions`, {
                          version: newVersion.version,
                          release_date: newVersion.release_date || new Date().toISOString().split("T")[0],
                          changes: newVersion.changes.split("\n").filter(c => c.trim()),
                          is_major: newVersion.is_major
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Version ajoutée");
                        setNewVersion({ version: "", release_date: "", changes: "", is_major: false });
                        fetchData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Erreur");
                      }
                    }}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter la version
                  </Button>
                </div>

                {/* Versions list */}
                <div className="space-y-3">
                  <h4 className="font-semibold">Historique des versions</h4>
                  {versions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucune version enregistrée</p>
                  ) : (
                    versions.map((v) => (
                      <div key={v.id} className={`p-4 rounded-xl border ${v.is_major ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={v.is_major ? 'bg-blue-500' : 'bg-gray-500'}>v{v.version}</Badge>
                            <span className="text-sm text-muted-foreground">{new Date(v.release_date).toLocaleDateString('fr-FR')}</span>
                            {v.is_major && <Badge variant="outline" className="text-blue-600 border-blue-300">Majeure</Badge>}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              if (window.confirm("Supprimer cette version ?")) {
                                await axios.delete(`${API}/admin/versions/${v.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success("Version supprimée");
                                fetchData();
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        {v.changes?.length > 0 && (
                          <ul className="list-disc list-inside text-sm text-charcoal space-y-1">
                            {v.changes.map((change, i) => (
                              <li key={i}>{change}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bugs Tab */}
          <TabsContent value="bugs">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bug className="w-5 h-5 text-orange-600" />
                  Suivi des bugs
                  <Badge className="ml-auto bg-orange-100 text-orange-700">
                    {bugs.filter(b => b.status === "open").length} ouverts
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add bug from ticket */}
                <div className="p-4 rounded-xl bg-orange-50 border border-orange-100 space-y-4">
                  <h4 className="font-semibold text-orange-800">Créer un bug depuis un ticket</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Titre du bug</Label>
                      <Input
                        placeholder="Description courte du bug"
                        value={newBug.title}
                        onChange={(e) => setNewBug({...newBug, title: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Sévérité</Label>
                      <Select value={newBug.severity} onValueChange={(v) => setNewBug({...newBug, severity: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Faible</SelectItem>
                          <SelectItem value="medium">Moyenne</SelectItem>
                          <SelectItem value="high">Haute</SelectItem>
                          <SelectItem value="critical">Critique</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Description détaillée du bug"
                      rows={3}
                      value={newBug.description}
                      onChange={(e) => setNewBug({...newBug, description: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Ticket source (optionnel)</Label>
                    <Select value={newBug.source_ticket_id} onValueChange={(v) => setNewBug({...newBug, source_ticket_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Lier à un ticket support" />
                      </SelectTrigger>
                      <SelectContent>
                        {tickets.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.subject} ({t.user_pseudo})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={async () => {
                      if (!newBug.title) {
                        toast.error("Titre requis");
                        return;
                      }
                      try {
                        await axios.post(`${API}/admin/bugs`, newBug, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Bug créé");
                        setNewBug({ title: "", description: "", severity: "medium", source_ticket_id: "" });
                        fetchData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Erreur");
                      }
                    }}
                    className="gap-2 bg-orange-500 hover:bg-orange-600"
                  >
                    <Plus className="w-4 h-4" />
                    Créer le bug
                  </Button>
                </div>

                {/* Bugs list */}
                <div className="space-y-3">
                  <h4 className="font-semibold">Liste des bugs</h4>
                  {bugs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun bug enregistré</p>
                  ) : (
                    bugs.map((bug) => (
                      <div key={bug.id} className={`p-4 rounded-xl border ${
                        bug.status === "resolved" ? 'bg-green-50 border-green-200' :
                        bug.status === "in_progress" ? 'bg-yellow-50 border-yellow-200' :
                        bug.severity === "critical" ? 'bg-red-50 border-red-200' :
                        'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-charcoal">{bug.title}</span>
                              <Badge className={
                                bug.severity === "critical" ? "bg-red-500" :
                                bug.severity === "high" ? "bg-orange-500" :
                                bug.severity === "medium" ? "bg-yellow-500" :
                                "bg-gray-400"
                              }>
                                {bug.severity === "critical" ? "Critique" :
                                 bug.severity === "high" ? "Haute" :
                                 bug.severity === "medium" ? "Moyenne" : "Faible"}
                              </Badge>
                              <Badge variant="outline" className={
                                bug.status === "resolved" ? "text-green-600 border-green-300" :
                                bug.status === "in_progress" ? "text-yellow-600 border-yellow-300" :
                                bug.status === "wont_fix" ? "text-gray-500 border-gray-300" :
                                "text-orange-600 border-orange-300"
                              }>
                                {bug.status === "resolved" ? "Résolu" :
                                 bug.status === "in_progress" ? "En cours" :
                                 bug.status === "wont_fix" ? "Non corrigé" : "Ouvert"}
                              </Badge>
                              {bug.resolved_in_version && (
                                <Badge variant="outline" className="text-blue-600 border-blue-300">
                                  Corrigé en v{bug.resolved_in_version}
                                </Badge>
                              )}
                            </div>
                            {bug.description && (
                              <p className="text-sm text-muted-foreground mt-1">{bug.description}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Créé le {new Date(bug.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Select
                              value={bug.status}
                              onValueChange={async (v) => {
                                try {
                                  const payload = { status: v };
                                  if (v === "resolved") {
                                    const version = window.prompt("Version de résolution (ex: 1.5.1)");
                                    if (version) payload.resolved_in_version = version;
                                  }
                                  await axios.put(`${API}/admin/bugs/${bug.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success("Statut mis à jour");
                                  fetchData();
                                } catch { toast.error("Erreur"); }
                              }}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Ouvert</SelectItem>
                                <SelectItem value="in_progress">En cours</SelectItem>
                                <SelectItem value="resolved">Résolu</SelectItem>
                                <SelectItem value="wont_fix">Non corrigé</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={async () => {
                                if (window.confirm("Supprimer ce bug ?")) {
                                  await axios.delete(`${API}/admin/bugs/${bug.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success("Bug supprimé");
                                  fetchData();
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
